import { z } from 'zod';
import { FINANCIAL_PRODUCT, LABELS } from '../constants';
import {
  requiredFieldError,
  requiredString,
  validateMobileAndPhone,
  validateMonthlyIncome,
  validateStringLength,
} from '../helper';
import { ApplicantType, EmploymentType } from '../types';
import { generateSchema } from './helper';
import {
  baseEmployment,
  employedDetails,
  income,
  mainIncome,
  openBankingIncome,
  otherDetails,
  pensionerDetails,
  selfEmployedDetails,
} from './model';

const employmentWithoutPensioner = z.object({
  type: z.enum([EmploymentType.EMPLOYED, EmploymentType.SELF_EMPLOYED], {
    errorMap: () => ({
      message: requiredFieldError(),
    }),
  }),
  employed: employedDetails.pick({ employerBusinessName: true }).optional(),
  selfEmployed: selfEmployedDetails.pick({ abn: true, entityName: true }).optional(),
});

const defaultSchema = {
  applicant: z.object({
    type: z.nativeEnum(ApplicantType),
    employment: baseEmployment.merge(
      z.object({
        type: z.nativeEnum(EmploymentType, {
          errorMap: () => ({
            message: requiredFieldError(),
          }),
        }),
        employed: employedDetails
          .merge(
            z.object({
              employerPhoneNumber: validateMobileAndPhone.optional(),
              jobTitle: requiredString().and(validateStringLength(LABELS.JOB_TITLE, 255)).optional(),
            }),
          )
          .optional(),
        selfEmployed: selfEmployedDetails
          .merge(
            z.object({
              jobTitle: requiredString().and(validateStringLength(LABELS.JOB_TITLE, 255)).optional(),
              phone: validateMobileAndPhone.optional(),
            }),
          )
          .optional(),
        pensioner: pensionerDetails.optional(),
      }),
    ),
    income: income.optional(),
    usingOpenBanking: z.boolean().optional(),
  }),
};

const actSlipIncomeVerification = {
  applicant: z.object({
    type: z.nativeEnum(ApplicantType),
    income: income.optional(),
    usingOpenBanking: z.boolean().optional(),
  }),
};

const flexIncomeVerification = {
  applicant: z.object({
    type: z.nativeEnum(ApplicantType),
    employment: employmentWithoutPensioner,
    income: mainIncome.superRefine((data, ctx) =>
      validateMonthlyIncome({
        data: data.primary,
        ctx,
        message: 'The household income provided does not meet eligibility for this finance product at this time',
        min: 5000,
        path: ['primary', 'amount'],
      }),
    ),
  }),
};

const electrify2515IncomeVerification = {
  applicant: z.object({
    type: z.nativeEnum(ApplicantType),
    employment: baseEmployment.merge(
      z.object({
        type: z.nativeEnum(EmploymentType, {
          errorMap: () => ({
            message: requiredFieldError(),
          }),
        }),
        other: otherDetails.optional(),
      }),
    ),
    income: income.optional(),
  }),
  payment: z
    .object({
      isCashApplication: z.boolean().optional(),
    })
    .optional(),
};

const brightePayEmployedDetails = employedDetails.merge(
  z.object({
    code: z.string().optional(),
    employerPhoneNumber: validateMobileAndPhone.optional(),
    jobTitle: requiredString().and(validateStringLength(LABELS.JOB_TITLE, 255)).optional(),
  }),
);

const brightePaySelfEmployedDetails = selfEmployedDetails.merge(
  z.object({
    jobTitle: requiredString().and(validateStringLength(LABELS.JOB_TITLE, 255)).optional(),
    code: z.string().optional(),
    phone: validateMobileAndPhone.optional(),
  }),
);

const brightePayPensionerDetails = pensionerDetails.merge(
  z.object({
    code: z.string().optional(),
  }),
);

const brightePayIncomeVerification = {
  applicant: z
    .object({
      type: z.nativeEnum(ApplicantType),
      employment: baseEmployment.merge(
        z.object({
          type: z.nativeEnum(EmploymentType, {
            errorMap: () => ({
              message: requiredFieldError(),
            }),
          }),
          other: otherDetails.optional(),
          employed: brightePayEmployedDetails.optional(),
          selfEmployed: brightePaySelfEmployedDetails.optional(),
          pensioner: brightePayPensionerDetails.optional(),
        }),
      ),
      income: openBankingIncome.optional(),
      usingOpenBanking: z.boolean().optional(),
    })
    .superRefine((val, ctx) => {
      const { employment } = val;
      const addIssue = (path: (string | number)[], message = requiredFieldError()) =>
        ctx.addIssue({ code: z.ZodIssueCode.custom, message, path });

      if (!val.usingOpenBanking) {
        switch (employment.type) {
          case EmploymentType.EMPLOYED:
            if (!employment?.employed?.employerPhoneNumber) addIssue(['employment', 'employed', 'employerPhoneNumber']);
            if (!employment?.employed?.jobTitle) addIssue(['employment', 'employed', 'jobTitle']);
            if (!employment?.employed?.code) addIssue(['employment', 'employed', 'code']);
            break;
          case EmploymentType.SELF_EMPLOYED:
            if (!employment?.selfEmployed?.phone) addIssue(['employment', 'selfEmployed', 'phone']);
            if (!employment?.selfEmployed?.code) addIssue(['employment', 'selfEmployed', 'code']);
            break;
          case EmploymentType.PENSIONER:
            if (!employment?.pensioner?.code) addIssue(['employment', 'pensioner', 'code']);
            break;
        }

        if (!val.income?.primary?.amount) addIssue(['income', 'primary', 'amount']);
        if (!val.income?.primary?.period) addIssue(['income', 'primary', 'period']);
        if (val.income?.isAdditionalIncome === undefined) addIssue(['income', 'isAdditionalIncome']);
        if (val.income?.isAdditionalIncome) {
          if (!val.income?.additional?.amount) addIssue(['income', 'additional', 'amount']);
          if (!val.income?.additional?.period) addIssue(['income', 'additional', 'period']);
        }
      }
    }),
};

const customSchemas = {
  [FINANCIAL_PRODUCT.ACT_SLIP]: actSlipIncomeVerification,
  [FINANCIAL_PRODUCT.FLEX]: flexIncomeVerification,
  [FINANCIAL_PRODUCT.ELECTRIFY_2515]: electrify2515IncomeVerification,
  [FINANCIAL_PRODUCT.BRIGHTE_PAY]: brightePayIncomeVerification,
};

type CustomSchemaType = typeof customSchemas;

export const incomeVerificationSchema = generateSchema<CustomSchemaType, typeof defaultSchema>({
  customSchemas,
  defaultSchema,
});
